import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Crown, Trophy } from 'lucide-react';
import { getInsurerBrokerDistribution } from '../../../../../api/requests';
import { formatCurrencyAmount, numberWithCommas } from '../../../../../utils/miscFunctions';

interface BrokerLeaderboardProps {
  companyId?: number;
}

interface BrokerRow {
  name: string;
  policyCount: number;
  premium: number;
}

/**
 * Broker Leaderboard — ranks the insurer's brokers by policy volume (with premium),
 * backed by the existing /chart/broker-distribution endpoint.
 */
const BrokerLeaderboard: React.FC<BrokerLeaderboardProps> = ({ companyId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['getInsurerBrokerDistribution', companyId],
    queryFn: () => getInsurerBrokerDistribution(companyId),
  });

  const distribution: any = data ?? {};
  const rows: BrokerRow[] = (distribution?.data?.labels ?? []).map((name: string, i: number) => ({
    name,
    policyCount: distribution?.data?.policyCount?.[i] ?? 0,
    premium: distribution?.data?.premium?.[i] ?? 0,
  }))
    .sort((a: BrokerRow, b: BrokerRow) => b.policyCount - a.policyCount)
    .slice(0, 8);

  const maxCount = rows.reduce((m, r) => Math.max(m, r.policyCount), 0) || 1;

  const rankColor = (index: number) => {
    if (index === 0) return 'text-yellow-500';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-amber-700';
    return 'text-gray-300 dark:text-gray-600';
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 dark:bg-darkBg dark:border-darkBg-border">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-primary-main" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Broker Leaderboard</h3>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 dark:bg-darkBg-border rounded animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No broker activity yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div key={row.name} className="flex items-center gap-3">
              <div className="w-6 flex items-center justify-center">
                {index === 0
                  ? <Crown className={`h-4 w-4 ${rankColor(index)}`} />
                  : <span className={`text-sm font-bold ${rankColor(index)}`}>{index + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800 dark:text-darkBg-mainText truncate">{row.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                    {numberWithCommas(row.policyCount)} · {formatCurrencyAmount(row.premium)}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-darkBg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-main rounded-full"
                    style={{ width: `${Math.max(4, (row.policyCount / maxCount) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrokerLeaderboard;
